import { useEffect, useState } from 'react';
import { FormSummary, duplicateFormApi, getFormWorkflow, listFormConnections } from '../../api/forms';
import { Modal } from '../../components/fs';

/**
 * What a duplicate should carry.
 *
 * The one question worth asking, because the two answers are both right and
 * neither is guessable from the button. A copy of the same form for a second
 * region wants the connections and the drawing; a copy being repointed
 * somewhere else wants the questions and nothing that would deliver to the
 * original's destinations the moment it is published. The tick is on, because
 * a duplicate that quietly does nothing is the more surprising of the two.
 *
 * It asks the form what it has first, so the sentence names real numbers and
 * the tick disappears entirely on a form with nothing to carry.
 */
export default function DuplicateFormDialog({ form, onClose, onDone }: {
  form: FormSummary;
  onClose: () => void;
  /** The copy exists; the caller decides where to send the admin next. */
  onDone: (copy: FormSummary) => void;
}) {
  const [carry, setCarry] = useState(true);
  const [has, setHas] = useState<{ connections: number; drawn: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    Promise.all([listFormConnections(form.id), getFormWorkflow(form.id)])
      .then(([c, w]) => { if (live) setHas({ connections: c.connections.length, drawn: !!w.workflow }); })
      // Not fatal: the copy is the point, and the checkbox can be asked
      // without knowing the count. Only the sentence loses its numbers.
      .catch(() => { if (live) setHas({ connections: 0, drawn: false }); });
    return () => { live = false; };
  }, [form.id]);

  const nothingToCarry = has && !has.connections && !has.drawn;

  async function go() {
    setBusy(true);
    setError('');
    try {
      const r = await duplicateFormApi(form.id, { workflow: carry && !nothingToCarry });
      onDone(r.form);
    } catch (e: any) {
      setError(e.message || 'Could not duplicate the form');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Duplicate “${form.name}”`}
      narrow
      onClose={onClose}
      footer={(
        <>
          <button className="ad-btn primary" disabled={busy} onClick={() => void go()}>
            {busy ? 'Duplicating…' : 'Duplicate'}
          </button>{' '}
          <button className="ad-btn secondary" disabled={busy} onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <p style={{ color: '#718096', fontSize: 13, margin: '0 0 14px', lineHeight: 1.55 }}>
        The copy is a new draft with the same questions, logic and settings. Submissions stay
        with “{form.name}”.
      </p>
      {error && <div className="ad-err">{error}</div>}
      {!has ? (
        <div style={{ color: '#718096', fontSize: 13, padding: '4px 0' }}>Checking what it delivers…</div>
      ) : nothingToCarry ? (
        <div style={{ color: '#718096', fontSize: 13, padding: '4px 0', lineHeight: 1.55 }}>
          This form has no connections and no workflow yet, so there is nothing else to bring.
        </div>
      ) : (
        <label className="fs-check">
          <input type="checkbox" checked={carry} onChange={(e) => setCarry(e.target.checked)} />
          <span>
            <b>Also copy what it does — {describe(has)}</b>
            <small>
              {carry
                ? 'Once you publish the copy it will deliver to the same destinations as the original.'
                : 'The copy will collect submissions and send them nowhere until you give it connections.'}
            </small>
          </span>
        </label>
      )}
    </Modal>
  );
}

/** "its 2 connections and its workflow", in the words of what is actually there. */
function describe({ connections, drawn }: { connections: number; drawn: boolean }): string {
  const conns = connections === 1 ? 'its connection' : `its ${connections} connections`;
  if (!connections) return 'its workflow';
  return drawn ? `${conns} and its workflow` : conns;
}
